import { zodResolver } from "@hookform/resolvers/zod";
import { Box, Button, Stack } from "@mui/joy";
import { ApiCompetition, ApiCustomScoringRoutePointsConfig } from "griptonite-api-client";
import { useEffect, useMemo } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { z } from "zod";

import { Route } from "@/core/types";
import {
  useRouteCustomScoringConfigQuery,
  useUpdateRouteCustomScoringConfigMutation,
} from "@/hooks/routes";

import CustomScoringTableInput from "../CompetitionProfile/CustomScoringTableInput";

const getSchema = (segmentsCount: number) =>
  z.object({
    config: z.object({
      successPoints: z.array(z.number().nonnegative()),
      ...(segmentsCount > 0
        ? {
            segment1Points: z.array(z.number().nonnegative()),
          }
        : {}),
      ...(segmentsCount > 1
        ? {
            segment2Points: z.array(z.number().nonnegative()),
          }
        : {}),
    }),
  });

export default function RouteCustomScoringForm({
  route,
  competition,
}: {
  route?: Route;
  competition?: ApiCompetition;
}) {
  const { data } = useRouteCustomScoringConfigQuery(route?.uuid);
  const schema = useMemo(() => getSchema(route?.segmentsCount || 0), [route?.segmentsCount]);
  const methods = useForm({
    defaultValues: data || {
      config: {
        successPoints: [],
        segment1Points: [],
        segment2Points: [],
      },
    },
    resolver: zodResolver(schema),
    mode: "onBlur",
  });

  useEffect(() => {
    if (data) {
      methods.reset(data);
    }
  }, [data, methods]);

  const mutation = useUpdateRouteCustomScoringConfigMutation(competition?.uuid);

  const onSubmit = async (values: { config: ApiCustomScoringRoutePointsConfig }) => {
    if (!route) {
      return;
    }
    await mutation.mutateAsync({
      routeUuid: route?.uuid,
      config: values.config,
    });
  };

  if (!route) {
    return null;
  }

  return (
    <FormProvider {...methods}>
      <Stack
        component="form"
        onSubmit={methods.handleSubmit(onSubmit)}
        flex={1}
        spacing={1}
        sx={{
          overflowY: "auto",
          minHeight: 0,
        }}
      >
        <Box sx={{ flex: 1 }}>
          <CustomScoringTableInput
            name="config"
            segmentsCount={route.segmentsCount || 0}
            smallInputs
            hasFixedPenalty={!!competition?.scoringSystemSettings.failedAttemptPenalty}
          />
        </Box>
        <Box sx={{ padding: 1, display: "flex", flexDirection: "column", gap: 1 }}>
          {methods.formState.isDirty ? (
            <Button
              type="submit"
              color="primary"
              loading={methods.formState.isSubmitting}
              loadingPosition="start"
              disabled={methods.formState.isSubmitting}
            >
              Save
            </Button>
          ) : null}
        </Box>
      </Stack>
    </FormProvider>
  );
}
